import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "../api/client";
import { useUser } from "../auth/useUser";
import { Header } from "../components/Header";
import { Sparkline } from "../components/charts/Sparkline";
import { TrendLine } from "../components/charts/TrendLine";
import { Donut } from "../components/charts/Donut";
import type { components } from "../api/types";

type AdminMetrics = components["schemas"]["AdminMetrics"];

// Tailwind theme colors as raw rgb-triplet strings so the chart
// SVGs can pass them directly to fill/stroke. Resolves the same
// CSS variables Tailwind uses (see frontend-admin/tailwind.config.js
// + index.css). Theme-aware via the runtime CSS-var read.
const ACCENT = "rgb(var(--accent))";
const COLOR_ACTIVE = "rgb(var(--accent))";
const COLOR_BANNED = "rgb(244 63 94)"; // rose-500
const COLOR_TOMBSTONED = "rgb(245 158 11)"; // amber-500
const COLOR_ADMIN = "rgb(34 197 94)"; // emerald-500
const COLOR_GRANTED = "rgb(34 197 94)"; // emerald-500
const COLOR_DECREMENTED = "rgb(244 63 94)"; // rose-500
const COLOR_REFUNDED = "rgb(245 158 11)"; // amber-500

export function Metrics() {
  const { data: me } = useUser();
  const q = useQuery({
    queryKey: ["admin", "metrics"],
    queryFn: async (): Promise<AdminMetrics> => {
      const { data, error } = await apiClient.GET("/api/v1/admin/metrics");
      if (error) throw error;
      return data as AdminMetrics;
    },
    // Auto-refresh every 60s so the page stays fresh without a
    // manual reload. refetchOnWindowFocus is already true at the
    // QueryClient level (main.tsx) so tab-focus also refreshes.
    refetchInterval: 60_000,
  });

  if (!me) return null;
  const m = q.data;

  return (
    <div className="min-h-screen flex flex-col">
      <Header user={me} />
      <main className="flex-1 max-w-6xl mx-auto w-full px-4 sm:px-8 py-12">
        <div className="flex items-end justify-between gap-4 mb-6">
          <h1 className="text-display font-medium text-ink-50 tracking-tightest">
            Metrics
          </h1>
          {m && (
            <span className="font-mono text-[10px] text-ink-400">
              computed <RelativeTime iso={m.as_of ?? ""} />
            </span>
          )}
        </div>

        {q.isLoading && <p className="text-sm text-ink-400">loading…</p>}
        {q.error && (
          <p className="text-sm text-rose-300">
            Failed to load metrics: {(q.error as Error).message}
          </p>
        )}
        {m && (
          <>
            <ActivePanel sessions={m.sessions} />
            <CounterRow metrics={m} />
            <ChartGrid metrics={m} />
          </>
        )}
      </main>
    </div>
  );
}

function ActivePanel({
  sessions,
}: {
  sessions: NonNullable<AdminMetrics["sessions"]>;
}) {
  const active = sessions.active_users ?? [];
  return (
    <section className="mb-10 border border-accent/40 rounded-md p-5 bg-ink-900/30 animate-fade-in">
      <div className="flex items-baseline gap-6 mb-4">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-widest text-ink-400">
            Active right now
          </p>
          <p className="text-3xl font-medium tabular-nums text-ink-50">
            {sessions.active_now ?? 0}
            <span className="text-sm text-ink-400 ml-2">users</span>
          </p>
        </div>
        <div className="text-ink-500">·</div>
        <div>
          <p className="font-mono text-[10px] uppercase tracking-widest text-ink-400">
            Logged in
          </p>
          <p className="text-3xl font-medium tabular-nums text-ink-200">
            {sessions.logged_in ?? 0}
            <span className="text-sm text-ink-400 ml-2">users</span>
          </p>
        </div>
      </div>
      {active.length === 0 ? (
        <p className="text-sm text-ink-400">No users active right now.</p>
      ) : (
        <ul className="text-sm divide-y divide-ink-800">
          {active.map((u) => (
            <li
              key={u.user_id}
              className="flex items-center gap-3 py-2"
            >
              <span className="font-mono text-[11px] text-ink-400 w-32 truncate">
                {u.user_id?.slice(0, 14)}
              </span>
              <span className="flex-1 text-ink-100 truncate">
                {u.display_name || u.email || "(unknown)"}
              </span>
              <span className="font-mono text-[11px] text-ink-400">
                last seen <RelativeTime iso={u.last_seen_at ?? ""} />
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function CounterRow({ metrics }: { metrics: AdminMetrics }) {
  const u = metrics.users!;
  const r = metrics.runs!;
  const c = metrics.credits!;
  const p = metrics.products!;
  return (
    <section className="mb-10 grid grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
      <Counter label="Users" value={u.total ?? 0}>
        <span className="text-[11px] text-ink-400">
          active {u.active ?? 0} · banned {u.banned ?? 0} · tombstoned{" "}
          {u.tombstoned ?? 0}
        </span>
      </Counter>
      <Counter label="Products" value={p.total ?? 0} />
      <Counter label="Runs" value={r.total ?? 0}>
        <span className="text-[11px] text-ink-400">
          24h {r.last_24h ?? 0} · 7d {r.last_7d ?? 0} · 30d{" "}
          {r.last_30d ?? 0}
        </span>
      </Counter>
      <Counter label="Credit pool" value={c.balance_sum ?? 0}>
        <span className="text-[11px] text-ink-400">
          ledger: +{c.granted_total ?? 0} / -{c.decremented_total ?? 0}{" "}
          / +{c.refunded_total ?? 0}
        </span>
      </Counter>
    </section>
  );
}

function Counter({
  label,
  value,
  children,
}: {
  label: string;
  value: number | bigint;
  children?: React.ReactNode;
}) {
  return (
    <div className="border border-ink-800 rounded-md p-4 bg-ink-900/30">
      <p className="font-mono text-[10px] uppercase tracking-widest text-ink-400 mb-1">
        {label}
      </p>
      <p className="text-2xl font-medium tabular-nums text-ink-50">
        {String(value)}
      </p>
      {children && <div className="mt-1">{children}</div>}
    </div>
  );
}

function ChartGrid({ metrics }: { metrics: AdminMetrics }) {
  const u = metrics.users!;
  const r = metrics.runs!;
  const c = metrics.credits!;
  return (
    <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Sparkline data={u.per_day_30d ?? []} label="Signups · 30d" accent={ACCENT} />
      <Sparkline data={r.per_day_30d ?? []} label="Runs · 30d" accent={ACCENT} />
      <TrendLine
        data={u.per_day_30d ?? []}
        label="Cumulative signups · 30d"
        accent={ACCENT}
      />
      <TrendLine
        data={r.per_day_30d ?? []}
        label="Cumulative runs · 30d"
        accent={ACCENT}
      />
      <Donut
        label="User status"
        slices={[
          { label: "Active", value: u.active ?? 0, color: COLOR_ACTIVE },
          { label: "Banned", value: u.banned ?? 0, color: COLOR_BANNED },
          {
            label: "Tombstoned",
            value: u.tombstoned ?? 0,
            color: COLOR_TOMBSTONED,
          },
          { label: "Admins", value: u.admins ?? 0, color: COLOR_ADMIN },
        ]}
        centerLabel="users"
      />
      <Donut
        label="Credit ledger flows"
        slices={[
          {
            label: "Granted",
            value: c.granted_total ?? 0,
            color: COLOR_GRANTED,
          },
          {
            label: "Decremented",
            value: c.decremented_total ?? 0,
            color: COLOR_DECREMENTED,
          },
          {
            label: "Refunded",
            value: c.refunded_total ?? 0,
            color: COLOR_REFUNDED,
          },
        ]}
        centerLabel="events"
      />
    </section>
  );
}

// RelativeTime renders an ISO timestamp as a "Ns ago" / "Nm ago"
// label that ticks once per second. Each instance owns its own
// 1Hz timer so the surrounding page doesn't re-render -- only the
// time string does.
function RelativeTime({ iso }: { iso: string }) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), 1000);
    return () => window.clearInterval(id);
  }, []);
  return <>{fmtAgo(iso)}</>;
}

// fmtAgo renders an ISO timestamp as "Ns ago" / "Nm ago" / "Nh ago"
// / "Nd ago". Pure function consumed by RelativeTime; kept exported
// internally so a static caller could still call it without the
// ticker.
function fmtAgo(iso: string): string {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (!t) return "—";
  const diffMs = Date.now() - t;
  if (diffMs < 0) return "just now";
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  return `${day}d ago`;
}
